'use client';

// Personal voice-mode settings (BUILD 2 WP2-8 step 11), mounted on the
// Voice settings page: hands-free timing preferences and spoken
// approvals when allowed. Each change saves immediately through the shared
// voice preferences API and config store.
//
// There is no voice picker and no language (U1): the voice follows the
// language, which is always automatic. Full duplex has no control here until
// the config says where it is available (a CCR on the C2-6 `voice` block).
// Nothing renders when the deployment has no voice mode.
import {
  fieldLabelClass,
  phoneSafeInputTextClass,
  textInputClass,
} from '@/components/ui/form-control';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useAppCatalogLocale } from '@/i18n/context';
import voiceCopy from '@/i18n/ns/voice';
import { cn } from '@/lib/utils';
import { useEffect, useId, useState } from 'react';
import type { VoicePrefs } from './contracts';
import { UNDO_CHOICES_MS } from './policy';
import { saveVoicePrefs } from './runtime';
import { Field, SwitchRow } from './settings-controls';
import { useVoiceConfig, voiceConfigStore } from './state';

type Copy = Readonly<Record<keyof typeof voiceCopy.en, string>>;
type Status = 'idle' | 'saved' | 'error';

// The Settings field (8 px radius, 16 px text on phones) like the rest of the page.
const SELECT_CLASS = cn(
  textInputClass,
  'rounded-sm px-3 focus-visible:outline-2 focus-visible:outline-(--ring)',
  phoneSafeInputTextClass,
);

function DurationSelect({
  label,
  value,
  onChange,
  copy,
  slot,
  choices: available,
}: {
  label: string;
  value: number;
  onChange(ms: number): void;
  copy: Copy;
  slot: string;
  choices: readonly number[];
}) {
  const id = useId();
  const number = new Intl.NumberFormat(useAppCatalogLocale());
  const choices = available.includes(value) ? available : [...available, value];
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={fieldLabelClass}>
        {label}
      </label>
      <select
        id={id}
        data-slot={slot}
        className={SELECT_CLASS}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {[...choices]
          .sort((a, b) => a - b)
          .map((ms) => (
            <option key={ms} value={ms}>
              {formatCopy(copy.undoSeconds, { seconds: number.format(ms / 1000) })}
            </option>
          ))}
      </select>
    </div>
  );
}

export function VoiceSettingsSection() {
  const copy = useNamespaceCopy(voiceCopy);
  const { voice, readAloud } = useVoiceConfig();
  const [status, setStatus] = useState<Status>('idle');
  const titleId = useId();
  useEffect(() => {
    if (!voiceConfigStore.current().voice) void voiceConfigStore.refresh();
  }, []);
  if (!voice || !readAloud?.available) return null;
  const prefs = voice.prefs;
  const save = (patch: Partial<VoicePrefs>) => {
    setStatus('idle');
    saveVoicePrefs(patch).then(
      () => setStatus('saved'),
      () => setStatus('error'),
    );
  };
  const undo = prefs.undoMs === 'always_ask' ? voice.undoMs : prefs.undoMs;
  return (
    <section
      data-slot="voice-settings"
      aria-labelledby={titleId}
      className="mt-10 flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h2 id={titleId} className="company-section-title">
          {copy.settingsTitle}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">{copy.settingsDescription}</p>
      </div>
      {!voice.policy.voiceMode ? (
        <p className="text-muted-foreground text-sm">{copy.errorPolicyOff}</p>
      ) : (
        <>
          <Field label={copy.undoEndpoint}>
            {prefs.undoMs !== 'always_ask' && (
              <DurationSelect
                slot="voice-undo-endpoint"
                copy={copy}
                choices={UNDO_CHOICES_MS}
                label={copy.undoWindow}
                value={undo.endpoint}
                onChange={(endpoint) => save({ undoMs: { endpoint } })}
              />
            )}
            <SwitchRow
              slot="voice-always-ask"
              label={copy.alwaysAsk}
              on={prefs.undoMs === 'always_ask'}
              onChange={(always) => save({ undoMs: always ? 'always_ask' : voice.undoMs })}
            />
          </Field>
          <DurationSelect
            slot="voice-endpoint-delay"
            copy={copy}
            choices={[500, 1000, 1500]}
            label={copy.waitBeforeSending}
            value={prefs.endpointDelayMs}
            onChange={(endpointDelayMs) => save({ endpointDelayMs })}
          />
          {voice.policy.spokenApprovals && (
            <SwitchRow
              slot="voice-spoken-approvals"
              label={copy.spokenApprovalsLabel}
              hint={copy.spokenApprovalsHint}
              on={prefs.spokenApprovals}
              onChange={(spokenApprovals) => save({ spokenApprovals })}
            />
          )}
        </>
      )}
      <p role="status" aria-live="polite" className="min-h-5 text-sm">
        {status === 'saved' ? copy.saved : ''}
      </p>
      {status === 'error' && (
        <p role="alert" className="company-error">
          {copy.saveFailed}
        </p>
      )}
    </section>
  );
}
